import React, { lazy } from 'react';

import { bool } from 'prop-types';

import { Navigate, Route } from 'react-router-dom';

import Protected from 'containers/Protected';

import { NEWSFEED, VIRTUAL_TOURS } from 'routes/constants';
import RedirectHelper from 'routes/MemberRoutes/RedirectHelper';

import CredentialWallet from 'modules/CredentialWallet/routes';
import MemberOrNonLoggedUserEventRedirect from 'modules/EventSchedulerV2/Member/components/MemberOrNonLoggedUserEventRedirect';

const AccountSettings = lazy(
  () =>
    import(
      /* webpackChunkName: 'account-settings' */ 'modules/AccountSettings'
    ),
);

const StudentGoals = lazy(
  () => import(/* webpackChunkName: 'student-goals' */ 'modules/StudentGoals'),
);

const CareerCenterPages = lazy(
  () =>
    import(
      /* webpackChunkName: 'career-center' */ 'modules/CareerCenter/routes'
    ),
);

const CoursePlanner = lazy(
  () =>
    import(/* webpackChunkName: 'course-planner' */ 'modules/CoursePlanner'),
);

const MessageCenter = lazy(
  () =>
    import(/* webpackChunkName: 'message-center' */ 'modules/MessageCenter'),
);

const Notifications = lazy(
  () =>
    import(
      /* webpackChunkName: 'notifications' */ 'modules/Notifications/pages/Notifications'
    ),
);

const Opportunities = lazy(
  () => import(/* webpackChunkName: 'opportunities' */ 'modules/Opportunities'),
);

const EmployerShowcase = lazy(
  () =>
    import(
      /* webpackChunkName: 'employer-showcase' */ 'modules/EmployerShowcase'
    ),
);

const StudentPlanListing = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-plan-listing' */ 'modules/StudentPlanListing'
    ),
);

const StudentProfile = lazy(
  () =>
    import(/* webpackChunkName: 'student-profile' */ 'modules/StudentProfile'),
);

const VisitNotification = lazy(
  () =>
    import(
      /* webpackChunkName: 'visit-notification' */ 'modules/VisitNotification'
    ),
);

const MemberOrNonLoggedUserEventListRedirect = lazy(
  () =>
    import(
      /* webpackChunkName: 'member-or-non-logged-in-user-event-list-redirect' */
      'modules/EventSchedulerV2/Member/components/MemberOrNonLoggedUserEventListRedirect'
    ),
);

const PersonalizedPlans = lazy(
  () =>
    import(
      /* webpackChunkName: 'personalized-plans' */ 'modules/PersonalizedPlans'
    ),
);

const StudentPathways = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-pathways' */ 'modules/StudentPathways'
    ),
);

const SchoolProfile = lazy(
  () =>
    import(/* webpackChunkName: 'school-profile' */ 'modules/SchoolProfile'),
);

const ShowcaseProfile = lazy(
  () =>
    import(
      /* webpackChunkName: 'showcase-profile' */ 'modules/EmployerShowcase/components/ShowcaseProfile'
    ),
);

const ClaimStudent = lazy(
  () =>
    import(
      /* webpackChunkName: 'claim-student' */ 'modules/Guardians/pages/ClaimStudent'
    ),
);

const LifeSimulator = lazy(
  () =>
    import(/* webpackChunkName: 'life-simulator' */ 'modules/LifeSimulator'),
);

const LearningTasks = lazy(
  () =>
    import(/* webpackChunkName: 'learning-tasks' */ 'modules/LearningTasks'),
);

const OrgProfile = lazy(
  () => import(/* webpackChunkName: 'org-profile' */ 'modules/OrgProfile'),
);

const SchoolSearch = lazy(
  () => import(/* webpackChunkName: 'school-search' */ 'modules/SchoolSearch'),
);

const MentorVideos = lazy(
  () => import(/* webpackChunkName: 'mentor-videos' */ 'modules/MentorVideos'),
);

const CollegeCosts = lazy(
  () =>
    import(
      /* webpackChunkName: 'college-costs' */ 'modules/CollegeCosts/routes'
    ),
);

const RecordCenterAlumni = lazy(
  () =>
    import(
      /* webpackChunkName: 'record-center' */ 'modules/RecordCenter/Student'
    ),
);

const RecordCenterMember = lazy(
  () =>
    import(
      /* webpackChunkName: 'record-center' */ 'modules/RecordCenter/Member'
    ),
);

const AlumniTakingSurveys = lazy(
  () =>
    import(
      /* webpackChunkName: 'alumni-taking-surveys' */ 'modules/AlumniTakingSurveys/routes'
    ),
);

const GuardianStudentCasefile = lazy(
  () =>
    import(
      /* webpackChunkName: 'guardian-student-casefile' */ 'modules/Guardians/pages/GuardianStudentCasefile'
    ),
);

const OrgRoutes = lazy(
  () => import(/* webpackChunkName: 'org' */ './OrgRoutes'),
);

const MemberDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: 'member-dashboard' */ 'modules/MemberDashboard'
    ),
);

const RecommendationLetterPortal = lazy(
  () =>
    import(
      /* webpackChunkName: 'recommendation-letter-portal' */ 'modules/CollegeApplicationManager/pages/RecommendationLetterPortal'
    ),
);

const ScholarshipMatching = lazy(
  () =>
    import(
      /* webpackChunkName: 'scholarship-matching' */ 'modules/ScholarshipMatching/pages/ScholarshipMatchPage'
    ),
);

const SupervisorExperienceTrackingPage = lazy(
  () =>
    import(
      /* webpackChunkName: 'supervisor-experience-tracking' */ 'modules/ExperienceTracking/pages/SupervisorExperienceTrackingPage'
    ),
);

const BYOPSponsorView = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-program-views' */ 'modules/IndustryPartnerProgramExploration/pages/BYOPSponsorView'
    ),
);

const DistrictConnectionsPage = lazy(
  () =>
    import(
      /* webpackChunkName: 'district-connections' */ 'modules/DistrictConnectionsPage'
    ),
);

const K12UsersEventRedirect = lazy(
  () =>
    import(
      /* webpackChunkName: 'k12-user-event-redirect' */ 'modules/EventSchedulerV2/components/K12UsersEventRedirect'
    ),
);

const EventSchedulerSurveys = lazy(
  () =>
    import(
      /* webpackChunkName: 'event-scheduler-surveys' */ 'modules/EventSchedulerV2/components/EventSchedulerSurveys'
    ),
);

const IPPEMemberRoutes = lazy(
  () =>
    import(
      /* webpackChunkName: 'ippe-member-routes' */ 'modules/IndustryPartnerProgramExploration/Member/routes'
    ),
);

const PublicEventRedirect = lazy(
  () =>
    import(
      /* webpackChunkName: 'public-event-redirect' */ 'modules/EventSchedulerV2/Public/pages/PublicEventRedirect'
    ),
);

const SurveyAnalytics = lazy(
  () =>
    import(
      /* webpackChunkName: 'survey-analytics' */ 'modules/SurveyAnalytics'
    ),
);

const MobileFileUpload = lazy(
  () =>
    import(
      /* webpackChunkName: 'mobile-file-upload' */ 'modules/MobileFileUpload/MobileFileUpload'
    ),
);

const AddAffiliation = lazy(
  () =>
    import(
      /* webpackChunkName: 'mobile-file-upload' */ 'modules/OnboardingAndLogin/components/AddAffiliation'
    ),
);

const BragSheetSurvey = lazy(
  () =>
    import(
      /* webpackChunkName: 'brag-sheet-survey' */ 'modules/BragSheetSurvey'
    ),
);

const StudentExperienceTrackingLandingPage = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-experience-tracking-landing-page' */ 'modules/ExperienceTracking/pages/StudentExperienceTrackingLandingPage'
    ),
);

const StudentExperienceTrackingDetailPage = lazy(
  () =>
    import(
      /* webpackChunkName: 'student-experience-tracking-detail-page' */ 'modules/ExperienceTracking/pages/StudentExperienceTrackingDetailPage'
    ),
);

const VirtualTours = lazy(
  () => import(/* webpackChunkName: 'virtual-tours' */ 'modules/VirtualTours'),
);

const Sitemap = lazy(
  () =>
    import(
      /* webpackChunkName: 'sitemap' */ 'modules/AccountSettings/components/Sitemap'
    ),
);

const MemberRoutes = ({ isAlumni }) => (
  <>
    <Route path="/login/k12" element={<Navigate to={NEWSFEED} />} />
    <Route path="/file-upload" element={<MobileFileUpload />} />
    <Route
      path="/public-event-redirect/:eventId/:districtId"
      element={<PublicEventRedirect />}
    />
    <Route
      path="/district-connections"
      element={
        <Protected userType="member">
          <DistrictConnectionsPage />
        </Protected>
      }
    />
    <Route
      path="/events/:eventId/member/redirect/"
      element={
        <Protected userType="member">
          <MemberOrNonLoggedUserEventRedirect />
        </Protected>
      }
    />
    <Route
      path="/event-scheduler/survey/:surveyInstanceId/:userSurveyId/:eventId"
      element={
        <Protected userType="member">
          <EventSchedulerSurveys />
        </Protected>
      }
    />
    <Route
      path="/event-scheduler/org/:districtId/redirect/"
      element={
        <Protected userType="member">
          <MemberOrNonLoggedUserEventListRedirect />
        </Protected>
      }
    />
    <Route
      path="/event-scheduler/org/:districtId/:schoolId/redirect/"
      element={
        <Protected userType="member">
          <MemberOrNonLoggedUserEventListRedirect />
        </Protected>
      }
    />
    <Route
      path={NEWSFEED}
      element={
        <Protected userType="member">
          <MemberDashboard />
        </Protected>
      }
    />
    <Route
      path="/account-settings/*"
      element={
        <Protected userType="member">
          <AccountSettings />
        </Protected>
      }
    />
    <Route
      path="/mentor-videos"
      element={
        <Protected userType="member">
          <MentorVideos />
        </Protected>
      }
    />
    <Route
      path="/notifications"
      element={
        <Protected userType="member">
          <Notifications />
        </Protected>
      }
    />
    <Route
      path="/organization-profile"
      element={
        <Protected userType="member">
          <OrgProfile />
        </Protected>
      }
    />
    <Route
      path="/recommendation-letter-portal"
      element={
        <Protected userType="member">
          <RecommendationLetterPortal />
        </Protected>
      }
    />
    <Route
      path="/program-exploration/*"
      element={
        <Protected userType="member">
          <IPPEMemberRoutes isAlumni={isAlumni} />
        </Protected>
      }
    />
    <Route
      path="/public-placement/:placementHashId/:programId"
      element={<RedirectHelper isBYOPRedirect />}
    />
    <Route
      path="/candidate-sourced/:placementHashId/:programId"
      element={
        <Protected userType="member">
          <BYOPSponsorView />
        </Protected>
      }
    />
    <Route
      path="/org/:orgId/*"
      element={
        <Protected userType="member">
          <OrgRoutes />
        </Protected>
      }
    />
    <Route
      path="/student-experience-supervision"
      element={
        <Protected userType="member">
          <SupervisorExperienceTrackingPage />
        </Protected>
      }
    />
    <Route
      path="/students/:studentId/profile"
      element={
        <Protected userType="member">
          <StudentProfile mode="view" type="student" fetchUserProfile />
        </Protected>
      }
    />
    <Route path="/profile">
      <Route
        path="edit"
        element={
          <Protected userType="member">
            <StudentProfile mode="edit" type="member" />
          </Protected>
        }
      />
      <Route
        path="preview"
        element={
          <Protected userType="member">
            <StudentProfile mode="preview" type="alum" />
          </Protected>
        }
      />
      <Route
        path="me"
        element={
          <Protected userType="member">
            <StudentProfile mode="preview" type="alum" />
          </Protected>
        }
      />
    </Route>
    <Route
      path="/survey-analytics/*"
      element={
        <Protected userType="member">
          <SurveyAnalytics />
        </Protected>
      }
    />
    <Route path="/employer-showcase">
      <Route
        path=":employerId"
        element={
          <Protected userType="member">
            <ShowcaseProfile />
          </Protected>
        }
      />
      <Route
        index
        element={
          <Protected userType="member">
            <EmployerShowcase />
          </Protected>
        }
      />
    </Route>
    {isAlumni ? (
      <>
        <Route
          path="/experience-tracking"
          element={
            <Protected userType="member">
              <StudentExperienceTrackingLandingPage />
            </Protected>
          }
        />
        <Route
          path="/experience-tracking/:id"
          element={
            <Protected userType="member">
              <StudentExperienceTrackingDetailPage />
            </Protected>
          }
        />
        <Route
          path="/alumni-profile/:studentId"
          element={
            <Protected userType="member">
              <StudentProfile mode="view" type="alum" />
            </Protected>
          }
        />
        <Route
          path="/alumni/survey/:isPreview/*"
          element={
            <Protected userType="member">
              <AlumniTakingSurveys />
            </Protected>
          }
        />
        <Route
          path="/learning"
          element={
            <Protected userType="member">
              <LearningTasks />
            </Protected>
          }
        />
        <Route
          path="/el/learning"
          element={
            <Protected userType="member">
              <LearningTasks isElementary />
            </Protected>
          }
        />
        <Route
          path="/schools/*"
          element={
            <Protected userType="member">
              <SchoolSearch />
            </Protected>
          }
        />
        <Route path="/school-profile">
          <Route
            path=":schoolId"
            element={
              <Protected userType="member">
                <SchoolProfile />
              </Protected>
            }
          />
          <Route
            path=":schoolId/:schoolName"
            element={
              <Protected userType="member">
                <SchoolProfile />
              </Protected>
            }
          />
        </Route>
        <Route
          path="/careers/*"
          element={
            <Protected userType="member">
              <CareerCenterPages />
            </Protected>
          }
        />
        <Route
          path="/opportunities/*"
          element={
            <Protected userType="member">
              <Opportunities />
            </Protected>
          }
        />
        <Route
          path="/record-requests/*"
          element={
            <Protected userType="member">
              <RecordCenterAlumni isAlumni />
            </Protected>
          }
        />
      </>
    ) : (
      <></>
    )}
    <Route
      path="/record-requests/*"
      element={
        <Protected userType="member">
          <RecordCenterMember />
        </Protected>
      }
    />
    <Route
      path="/claim-student"
      element={
        <Protected userType="guardian">
          <ClaimStudent />
        </Protected>
      }
    />
    <Route
      path="/events/:eventId/k12-user/redirect/"
      element={
        <Protected userType="guardian">
          <K12UsersEventRedirect />
        </Protected>
      }
    />
    <Route
      path="/events/event-schedule/details/:eventId/"
      element={
        <Protected userType="guardian">
          <K12UsersEventRedirect switchK12Admin />
        </Protected>
      }
    />
    <Route
      path="/account-settings/*"
      element={
        <Protected userType="guardian">
          <AccountSettings />
        </Protected>
      }
    />
    <Route
      path="/course-planner/:studentPlanUUID/:requirementUUID"
      element={
        <Protected userType="guardian">
          <CoursePlanner />
        </Protected>
      }
    />
    <Route
      path="/course-plans/:studentId"
      element={
        <Protected userType="guardian">
          <StudentPlanListing />
        </Protected>
      }
    />
    <Route
      path="/careers/*"
      element={
        <Protected userType="guardian">
          <CareerCenterPages />
        </Protected>
      }
    />
    <Route
      path="/plans/*"
      element={
        <Protected userType="guardian">
          <PersonalizedPlans />
        </Protected>
      }
    />
    <Route
      path="/el/learning/:studentId/*"
      element={
        <Protected userType="guardian">
          <LearningTasks isElementary />
        </Protected>
      }
    />
    <Route
      path="/learning/:studentId/*"
      element={
        <Protected userType="guardian">
          <LearningTasks />
        </Protected>
      }
    />
    <Route
      path="/goals/*"
      element={
        <Protected userType="guardian">
          <StudentGoals />
        </Protected>
      }
    />
    <Route
      path="/opportunities/*"
      element={
        <Protected userType="guardian">
          <Opportunities />
        </Protected>
      }
    />
    <Route
      path="/notifications"
      element={
        <Protected userType="guardian">
          <Notifications />
        </Protected>
      }
    />
    <Route
      path="/visit-notification/:notificationId"
      element={
        <Protected userType="guardian">
          <VisitNotification />
        </Protected>
      }
    />
    <Route
      path="/messages/*"
      element={
        <Protected userType="guardian">
          <MessageCenter />
        </Protected>
      }
    />
    <Route
      path="/students/:studentId/profile"
      element={
        <Protected userType="guardian">
          <StudentProfile mode="view" type="student" fetchUserProfile />
        </Protected>
      }
    />
    <Route
      path="/schools/*"
      element={
        <Protected userType="guardian">
          <SchoolSearch />
        </Protected>
      }
    />
    <Route
      path="/game-of-life/:studentId/:scenarioUuid"
      element={
        <Protected userType="guardian">
          <LifeSimulator />
        </Protected>
      }
    />
    <Route path="/school-profile">
      <Route
        path=":schoolId"
        element={
          <Protected userType="guardian">
            <SchoolProfile />
          </Protected>
        }
      />
      <Route
        path=":schoolId/:schoolName"
        element={
          <Protected userType="guardian">
            <SchoolProfile />
          </Protected>
        }
      />
    </Route>
    {/* college-costs is reserved for students only, but its sub-paths should be allowed */}
    <Route
      path="/college-costs"
      element={
        <Protected userType="member">
          <Navigate to={NEWSFEED} />
        </Protected>
      }
    />
    <Route
      path="/college-costs/*"
      element={
        <Protected userType="guardian">
          <CollegeCosts />
        </Protected>
      }
    />
    <Route
      path="/scholarship-matching"
      element={
        <Protected userType="guardian">
          <ScholarshipMatching />
        </Protected>
      }
    />
    <Route
      path="/student-pathways/*"
      element={
        <Protected userType="guardian">
          <StudentPathways />
        </Protected>
      }
    />
    <Route
      path="/:casefileName/:studentId/*"
      element={
        <Protected userType="guardian">
          <GuardianStudentCasefile />
        </Protected>
      }
    />
    <Route path="/add-affiliation" element={<AddAffiliation />} />
    <Route
      path="/onboarding/add-affiliation"
      element={<RedirectHelper isAddAffiliationRedirect />}
    />
    <Route
      path="/onboarding/guardian/:hashId"
      element={<RedirectHelper isOnboardingGuardianRedirect />}
    />
    <Route
      path="/brag-sheet-survey/*"
      element={
        <Protected userType="guardian">
          <BragSheetSurvey />
        </Protected>
      }
    />
    <Route
      path="/onboarding/recommender/:hashId"
      element={<RedirectHelper isRecommenderRedirect />}
    />
    <Route
      path={`${VIRTUAL_TOURS}/*`}
      element={
        <Protected userType="guardian">
          <VirtualTours />
        </Protected>
      }
    />
    <Route
      path="/credential-wallet/:studentId/*"
      element={<CredentialWallet mode="public" />}
    />
    <Route
      path="/sitemap"
      element={
        <Protected>
          <Sitemap />
        </Protected>
      }
    />
  </>
);

MemberRoutes.propTypes = {
  isAlumni: bool.isRequired,
};

export default MemberRoutes;
